import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  message,
  Row,
  Col,
  Popconfirm,
  Upload,
  InputNumber,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ToolOutlined,
  DownloadOutlined,
  UploadOutlined,
  PrinterOutlined,
  SearchOutlined,
  ReloadOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import dataService from "../networks/api/service/dataService";
import CustomTable from "../components/TableComponent/CustomTable";

const { Option } = Select;

/* ─── Design tokens (matches Employee page) ─────────────── */
const T = {
  orange:     "#F97316",
  orangeLight:"#FFF7ED",
  orangeDark: "#C2500A",
  dark:       "#0F172A",
  mid:        "#334155",
  muted:      "#64748B",
  border:     "#E2E8F0",
  white:      "#FFFFFF",
  success:    "#16A34A",
  danger:     "#DC2626",
  warning:    "#D97706",
};

/* ─── Injected global styles ─────────────────────────────── */
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

  .asset-page * { box-sizing: border-box; }

  .asset-page {
    font-family: 'DM Sans', sans-serif;
    background: #F8FAFC;
    min-height: 100vh;
    color: ${T.dark};
  }

  .asset-header {
    background: ${T.white};
    padding: 20px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 3px solid ${T.orange};
  }
  .asset-header-title {
    font-family: 'Syne', sans-serif;
    font-size: 26px;
    font-weight: 800;
    color: ${T.orangeDark};
    margin: 0;
    letter-spacing: -0.5px;
  }
  .asset-header-title span { color: ${T.orange}; }

  .asset-stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
    padding: 28px 32px 0;
  }
  .asset-stat-card {
    background: ${T.white};
    border-radius: 16px;
    padding: 20px 24px;
    border: 1px solid ${T.border};
    position: relative;
    overflow: hidden;
    transition: box-shadow 0.2s, transform 0.2s;
  }
  .asset-stat-card:hover {
    box-shadow: 0 8px 28px rgba(249,115,22,0.12);
    transform: translateY(-2px);
  }
  .asset-stat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: ${T.orange};
    border-radius: 16px 16px 0 0;
  }
  .asset-stat-label {
    font-size: 12px;
    font-weight: 500;
    color: ${T.muted};
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin-bottom: 6px;
  }
  .asset-stat-value {
    font-family: 'Syne', sans-serif;
    font-size: 36px;
    font-weight: 800;
    color: ${T.dark};
    line-height: 1;
  }
  .asset-stat-icon {
    position: absolute;
    right: 18px;
    top: 18px;
    font-size: 28px;
    color: ${T.orange};
    opacity: 0.18;
  }

  .asset-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 24px 32px 0;
  }
  .asset-toolbar-right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .asset-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 18px;
    height: 38px;
    border-radius: 10px;
    font-size: 13.5px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    border: none;
    transition: all 0.18s;
    white-space: nowrap;
  }
  .asset-btn-primary {
    background: ${T.orange};
    color: ${T.white};
    box-shadow: 0 2px 10px rgba(249,115,22,0.3);
  }
  .asset-btn-primary:hover { background: ${T.orangeDark}; }
  .asset-btn-outline {
    background: ${T.white};
    color: ${T.dark};
    border: 1.5px solid ${T.border};
  }
  .asset-btn-outline:hover { border-color: ${T.orange}; color: ${T.orange}; }
  .asset-btn-ghost {
    background: transparent;
    color: ${T.muted};
    border: 1.5px solid ${T.border};
  }
  .asset-btn-ghost:hover { background: ${T.orangeLight}; color: ${T.orange}; border-color: ${T.orange}; }

  .asset-search-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  .asset-search-wrap .anticon {
    position: absolute;
    left: 12px;
    color: ${T.muted};
    z-index: 1;
    pointer-events: none;
  }
  .asset-search-wrap .ant-input {
    padding-left: 36px !important;
    height: 38px;
    border-radius: 10px !important;
    border: 1.5px solid ${T.border} !important;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    width: 240px;
  }
  .asset-search-wrap .ant-input:focus {
    border-color: ${T.orange} !important;
    box-shadow: 0 0 0 3px rgba(249,115,22,0.12) !important;
  }

  .emp-search-input {
    padding: 0 14px 0 36px;
    height: 38px;
    border-radius: 10px;
    border: 1.5px solid ${T.border};
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    color: ${T.dark};
    width: 240px;
    outline: none;
    transition: border-color 0.18s, box-shadow 0.18s;
    background: ${T.white};
  }
  .emp-search-input:focus { border-color: ${T.orange}; box-shadow: 0 0 0 3px rgba(249,115,22,0.12); }

  .asset-table-wrap {
    margin: 24px 32px 32px;
    background: ${T.white};
    border-radius: 20px;
    border: 1px solid ${T.border};
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(15,23,42,0.05);
  }
  .asset-table-wrap .ant-table {
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
  }
  .asset-table-wrap .ant-table-thead > tr > th {
    background: ${T.dark} !important;
    color: ${T.white} !important;
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 12.5px;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    border-bottom: none !important;
    padding: 14px 18px !important;
  }
  .asset-table-wrap .ant-table-thead > tr > th::before { display: none !important; }
  .asset-table-wrap .ant-table-tbody > tr > td {
    padding: 14px 18px !important;
    border-bottom: 1px solid #F1F5F9 !important;
    color: ${T.dark};
    vertical-align: middle;
  }
  .asset-table-wrap .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
  .asset-table-wrap .ant-table-tbody > tr:hover > td { background: ${T.orangeLight} !important; }
  .asset-table-wrap .ant-table-tbody > tr:nth-child(even) > td { background: #FAFAFA; }
  .asset-table-wrap .ant-table-tbody > tr:nth-child(even):hover > td { background: ${T.orangeLight} !important; }
  .asset-table-wrap .ant-pagination {
    padding: 16px 24px !important;
    margin: 0 !important;
    border-top: 1px solid ${T.border};
  }
  .asset-table-wrap .ant-pagination-item-active {
    background: ${T.orange} !important;
    border-color: ${T.orange} !important;
  }
  .asset-table-wrap .ant-pagination-item-active a { color: ${T.white} !important; }

  /* Modal */
  .ant-modal-content { border-radius: 20px !important; overflow: hidden; }
  .ant-modal-header { border-bottom: 1px solid ${T.border} !important; padding: 20px 24px !important; }
  .ant-modal-title {
    font-family: 'Syne', sans-serif !important;
    font-weight: 700 !important;
    font-size: 18px !important;
    color: ${T.dark} !important;
  }
  .ant-modal-footer { border-top: 1px solid ${T.border} !important; padding: 16px 24px !important; }
  .ant-btn-primary { background: ${T.orange} !important; border-color: ${T.orange} !important; border-radius: 8px !important; }
  .ant-btn { border-radius: 8px !important; font-family: 'DM Sans', sans-serif !important; }
  .ant-form-item-label label {
    font-family: 'DM Sans', sans-serif !important;
    font-weight: 500 !important;
    color: ${T.mid} !important;
    font-size: 13px !important;
  }
  .ant-input, .ant-select-selector, .ant-input-number, .ant-picker {
    border-radius: 10px !important;
    border-color: ${T.border} !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .ant-input:focus, .ant-select-focused .ant-select-selector,
  .ant-input-number:focus, .ant-picker:focus {
    border-color: ${T.orange} !important;
    box-shadow: 0 0 0 3px rgba(249,115,22,0.12) !important;
  }
`;

const AssetManagement = () => {
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const tableRef = useRef();

  const assetCategories = [
    "Laptop","Desktop","Printer","AC","Chair","Table",
    "CCTV","Walky Talky","Projector","TV Screen","Mic Set","Other",
  ];
  const workingStatusOptions = ["Working", "Not Working", "Under Maintenance"];

  useEffect(() => { fetchAssets(); }, []);

  const fetchAssets = async () => {
    try {
      const response = await dataService.getAssets();
      const apiAssets = response?.data || [];
      const mappedAssets = apiAssets.map((asset) => ({
        id: asset._id,
        assetName: asset.assetName,
        category: asset.category,
        count: Number(asset.count),
        dateOfPurchase: asset.dateOfPurchase,
        workingStatus: asset.workingStatus,
        remarks: asset.remarks || "",
        assignToPersonOrAdmin: asset.assignToPersonOrAdmin || "",
      }));
      setAssets(mappedAssets);
      setFilteredAssets(mappedAssets);
    } catch (error) {
      message.error("Failed to fetch assets");
      console.error(error);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    if (!value) { setFilteredAssets(assets); return; }
    setFilteredAssets(assets.filter((asset) =>
      asset.assetName.toLowerCase().includes(value) ||
      asset.category.toLowerCase().includes(value) ||
      asset.workingStatus.toLowerCase().includes(value) ||
      (asset.remarks && asset.remarks.toLowerCase().includes(value)) ||
      (asset.assignToPersonOrAdmin && asset.assignToPersonOrAdmin.toLowerCase().includes(value))
    ));
  };

  const handleExportToExcel = () => {
    if (!assets.length) { message.warning("No data to export"); return; }
    const dataToExport = assets.map((asset) => ({
      "Asset Name": asset.assetName,
      Category: asset.category,
      Count: asset.count,
      "Date of Purchase": dayjs(asset.dateOfPurchase).format("DD/MM/YYYY"),
      "Working Status": asset.workingStatus,
      Remarks: asset.remarks || "",
      "Assigned To": asset.assignToPersonOrAdmin || "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Assets");
    XLSX.writeFile(workbook, `Assets_${dayjs().format("YYYYMMDD")}.xlsx`);
    message.success("Data exported successfully!");
  };

  const handleBulkAssetUpload = async (file) => {
    try {
      await dataService.uploadBulkAssets(file);
      message.success("Assets uploaded successfully");
      fetchAssets();
    } catch (error) {
      console.error(error);
      message.error("Failed to upload assets");
    }
    return false;
  };

  const getSummaryStats = () => {
    const total = assets.reduce((sum, a) => sum + a.count, 0);
    const working = assets.filter(a => a.workingStatus === "Working").reduce((s, a) => s + a.count, 0);
    const notWorking = assets.filter(a => a.workingStatus === "Not Working").reduce((s, a) => s + a.count, 0);
    const maintenance = assets.filter(a => a.workingStatus === "Under Maintenance").reduce((s, a) => s + a.count, 0);
    return { total, working, notWorking, maintenance };
  };

  const stats = getSummaryStats();

  const showModal = (asset = null) => {
    setEditingAsset(asset);
    if (asset) {
      form.setFieldsValue({
        ...asset,
        assignment: asset.assignToPersonOrAdmin,
        dateOfPurchase: dayjs(asset.dateOfPurchase),
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingAsset(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        assetName: values.assetName,
        category: values.category,
        count: Number(values.count),
        dateOfPurchase: values.dateOfPurchase.format("YYYY-MM-DD"),
        workingStatus: values.workingStatus,
        Remarks: values.remarks || "",
        assignment: values.assignment,
      };
      if (editingAsset) {
        await dataService.editAssets(editingAsset.id, payload);
        message.success("Asset updated successfully!");
        handleCancel(); fetchAssets(); return;
      }
      await dataService.createAsset(payload);
      message.success("Asset created successfully!");
      handleCancel(); fetchAssets();
    } catch (error) {
      message.error("Failed to save asset");
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await dataService.deleteAssets(id);
      message.success("Asset deleted successfully!");
      fetchAssets();
    } catch (error) {
      message.error("Failed to delete asset");
      console.error(error);
    }
  };

  const columns = [
    {
      title: "Asset Name",
      dataIndex: "assetName",
      key: "assetName",
      sorter: (a, b) => a.assetName.localeCompare(b.assetName),
      width: 200,
      render: (name) => <span style={{ fontWeight: 500 }}>{name}</span>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      filters: assetCategories.map((cat) => ({ text: cat, value: cat })),
      onFilter: (value, record) => record.category === value,
      width: 160,
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      align: "center",
      sorter: (a, b) => a.count - b.count,
      width: 100,
    },
    {
      title: "Date of Purchase",
      dataIndex: "dateOfPurchase",
      key: "dateOfPurchase",
      align: "center",
      sorter: (a, b) => new Date(a.dateOfPurchase) - new Date(b.dateOfPurchase),
      render: (date) => <span style={{ color: T.muted }}>{dayjs(date).format("DD/MM/YYYY")}</span>,
      width: 160,
    },
    {
      title: "Status",
      dataIndex: "workingStatus",
      key: "workingStatus",
      align: "center",
      filters: workingStatusOptions.map((s) => ({ text: s, value: s })),
      onFilter: (value, record) => record.workingStatus === value,
      render: (status) => {
        const styles = {
          "Working":          { bg: "#F0FDF4", color: T.success, dot: T.success },
          "Not Working":      { bg: "#FEF2F2", color: T.danger,  dot: T.danger  },
          "Under Maintenance":{ bg: "#FFFBEB", color: T.warning, dot: T.warning },
        };
        const s = styles[status] || styles["Working"];
        return (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "3px 10px", borderRadius: 20,
            background: s.bg, color: s.color,
            fontSize: 12, fontWeight: 500,
          }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.dot, display: "inline-block" }} />
            {status}
          </span>
        );
      },
    },
    {
      title: "Assigned Person / Room",
      dataIndex: "assignToPersonOrAdmin",
      key: "assignToPersonOrAdmin",
      align: "center",
      width: 220,
      render: (v) => <span style={{ color: v ? T.dark : T.muted }}>{v || "—"}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size={8}>
          <button
            onClick={() => showModal(record)}
            style={{
              height: 30, padding: "0 12px", fontSize: 13,
              borderRadius: 8, background: T.white, color: T.dark,
              border: `1.5px solid ${T.border}`, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 5,
              fontFamily: "'DM Sans', sans-serif",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.dark; }}
          >
            <EditOutlined /> Edit
          </button>
          <Popconfirm
            title="Delete this asset?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes" cancelText="No"
          >
            <button
              style={{
                height: 30, padding: "0 12px", fontSize: 13,
                borderRadius: 8, background: "#FEF2F2", color: T.danger,
                border: "1.5px solid #FECACA", cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 5,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <DeleteOutlined /> Delete
            </button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <style>{globalStyles}</style>
      <div className="asset-page">

        {/* Header */}
        <div className="asset-header">
          <h1 className="asset-header-title">Asset <span>Management</span></h1>
        </div>

        {/* Stat Cards */}
        <div className="asset-stat-grid">
          <div className="asset-stat-card">
            <div className="asset-stat-label">Total Assets</div>
            <div className="asset-stat-value">{stats.total}</div>
            <AppstoreOutlined className="asset-stat-icon" />
          </div>
          <div className="asset-stat-card">
            <div className="asset-stat-label">Working</div>
            <div className="asset-stat-value" style={{ color: T.success }}>{stats.working}</div>
            <CheckCircleOutlined className="asset-stat-icon" />
          </div>
          <div className="asset-stat-card">
            <div className="asset-stat-label">Not Working</div>
            <div className="asset-stat-value" style={{ color: T.danger }}>{stats.notWorking}</div>
            <CloseCircleOutlined className="asset-stat-icon" />
          </div>
          <div className="asset-stat-card">
            <div className="asset-stat-label">Under Maintenance</div>
            <div className="asset-stat-value" style={{ color: T.warning }}>{stats.maintenance}</div>
            <ToolOutlined className="asset-stat-icon" />
          </div>
        </div>

        {/* Toolbar */}
        <div className="asset-toolbar">
          <button className="asset-btn asset-btn-primary" onClick={() => showModal()}>
            <PlusOutlined /> Add New Asset
          </button>
          <button className="asset-btn asset-btn-outline" onClick={handleExportToExcel}>
            <DownloadOutlined /> Export Excel
          </button>
          <button className="asset-btn asset-btn-outline">
            <a
              href="/final asset.xlsx"
              download="Asset_Bulk_Upload_Template.xlsx"
              style={{ color: "inherit", textDecoration: "none", display: "flex", alignItems: "center", gap: 7 }}
            >
              <DownloadOutlined /> Sample File
            </a>
          </button>
          <Upload beforeUpload={handleBulkAssetUpload} showUploadList={false} accept=".xlsx,.xls">
            <button className="asset-btn asset-btn-ghost">
              <UploadOutlined /> Import Excel
            </button>
          </Upload>
          <button className="asset-btn asset-btn-ghost" onClick={() => window.print()}>
            <PrinterOutlined /> Print
          </button>
          <button className="asset-btn asset-btn-ghost" onClick={() => { fetchAssets(); setSearchText(""); message.success("Refreshed!"); }}>
            <ReloadOutlined /> Refresh
          </button>

          <div className="asset-toolbar-right">
            <div className="asset-search-wrap">
              <SearchOutlined />
               <input
                className="emp-search-input"
                placeholder="Search assets…"
                value={searchText}
                onChange={handleSearch}
                allowClear
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="asset-table-wrap">
          <CustomTable
            ref={tableRef}
            columns={columns}
            dataSource={filteredAssets}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} assets` }}
            scroll={{ x: true }}
          />
        </div>

        {/* Modal */}
        <Modal
          title={editingAsset ? "Edit Asset" : "Add New Asset"}
          visible={isModalVisible}
          onOk={handleSubmit}
          onCancel={handleCancel}
          okText={editingAsset ? "Update" : "Add Asset"}
          width={560}
        >
          <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
            <Form.Item name="assetName" label="Asset Name" rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="Enter asset name" />
            </Form.Item>
            <Form.Item name="category" label="Category" rules={[{ required: true, message: "Required" }]}>
              <Select placeholder="Select category">
                {assetCategories.map((cat) => <Option key={cat} value={cat}>{cat}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="count" label="Count" rules={[{ required: true, message: "Required" }, { type: "number", min: 1, message: "Minimum 1" }]}>
              <InputNumber min={1} style={{ width: "100%" }} placeholder="Enter count" />
            </Form.Item>
            <Form.Item name="assignment" label="Assigned Person / Room" rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="E.g. Anurag / Room 101" />
            </Form.Item>
            <Form.Item name="dateOfPurchase" label="Date of Purchase" rules={[{ required: true, message: "Required" }]}>
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="workingStatus" label="Working Status" rules={[{ required: true, message: "Required" }]}>
              <Select placeholder="Select status">
                {workingStatusOptions.map((s) => <Option key={s} value={s}>{s}</Option>)}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default AssetManagement;